import { CircleAlert } from 'lucide-react';
import { useId, type InputHTMLAttributes, type Ref } from 'react';
import { cn } from '../../lib/cn';

export interface CampoProps extends InputHTMLAttributes<HTMLInputElement> {
  rotulo: string;
  dica?: string;
  erro?: string;
  ref?: Ref<HTMLInputElement>;
}

/**
 * Campo de formulário acessível: o rótulo é ligado ao campo, e a dica e o erro
 * são lidos pelo leitor de tela (aria-describedby / aria-invalid).
 */
export function Campo({ rotulo, dica, erro, required, className, id, ...props }: CampoProps) {
  const idGerado = useId();
  const idCampo = id ?? idGerado;
  const idDica = dica ? `${idCampo}-dica` : undefined;
  const idErro = erro ? `${idCampo}-erro` : undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={idCampo} className="text-sm font-medium text-texto">
        {rotulo}
        {required && (
          <span className="ml-0.5 text-perigo" aria-hidden="true">
            *
          </span>
        )}
      </label>
      <input
        id={idCampo}
        required={required}
        aria-invalid={erro ? true : undefined}
        aria-describedby={[idDica, idErro].filter(Boolean).join(' ') || undefined}
        className={cn(
          'h-11 rounded-lg border border-borda bg-superficie px-3 text-texto placeholder:text-texto-suave',
          erro && 'border-perigo',
          className,
        )}
        {...props}
      />
      {dica && (
        <p id={idDica} className="text-sm text-texto-suave">
          {dica}
        </p>
      )}
      {erro && (
        <p id={idErro} className="flex items-center gap-1.5 text-sm text-perigo">
          <CircleAlert className="size-4 shrink-0" aria-hidden="true" />
          {erro}
        </p>
      )}
    </div>
  );
}
